import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';
import type { PermissionCategory, PermissionGroup, RolePermissionMapping, TenantRole, MyPermissionsResponse } from '@pm/shared';

interface ListResponse<T> {
  success: boolean;
  data: T;
}

export function useAllPermissions() {
  return useQuery<ListResponse<PermissionCategory[]>>({
    queryKey: ['admin', 'permissions'],
    queryFn: () => apiClient.get('/api/v1/admin/permissions'),
  });
}

export function usePermissionGroups() {
  return useQuery<ListResponse<PermissionGroup[]>>({
    queryKey: ['admin', 'permission-groups'],
    queryFn: () => apiClient.get('/api/v1/admin/permission-groups'),
  });
}

export function useCreatePermissionGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string; permissions: string[]; information_class?: string }) =>
      apiClient.post('/api/v1/admin/permission-groups', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'permission-groups'] }),
  });
}

export function useUpdatePermissionGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/api/v1/admin/permission-groups/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'permission-groups'] }),
  });
}

export function useDeletePermissionGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/admin/permission-groups/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'permission-groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'role-mappings'] });
    },
  });
}

export function useRoleMappings() {
  return useQuery<ListResponse<RolePermissionMapping[]>>({
    queryKey: ['admin', 'role-mappings'],
    queryFn: () => apiClient.get('/api/v1/admin/role-mappings'),
  });
}

export function useSetRoleMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleCode, groupIds }: { roleCode: string; groupIds: string[] }) =>
      apiClient.put(`/api/v1/admin/role-mappings/${roleCode}`, { permission_group_ids: groupIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'role-mappings'] }),
  });
}

// --- Tenant Roles ---

export function useTenantRoles() {
  return useQuery<ListResponse<TenantRole[]>>({
    queryKey: ['admin', 'tenant-roles'],
    queryFn: () => apiClient.get('/api/v1/admin/tenant-roles'),
  });
}

export function useCreateTenantRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { code: string; name: string; description?: string; category?: string; sort_order?: number }) =>
      apiClient.post('/api/v1/admin/tenant-roles', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenant-roles'] }),
  });
}

export function useUpdateTenantRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/api/v1/admin/tenant-roles/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenant-roles'] }),
  });
}

export function useDeleteTenantRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/admin/tenant-roles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenant-roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'role-mappings'] });
    },
  });
}

// --- User Permission Groups ---

export function useUserPermissionGroups(email?: string) {
  return useQuery<ListResponse<Array<{ id: string; employee_email: string; permission_group_id: string; is_super_admin: boolean; group_name?: string }>>>({
    queryKey: ['admin', 'user-permission-groups', email ?? 'all'],
    queryFn: () => apiClient.get('/api/v1/admin/user-permission-groups', email ? { email } : undefined),
  });
}

export function useSetUserPermissionGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, groupIds, isSuperAdmin }: { email: string; groupIds: string[]; isSuperAdmin: boolean }) =>
      apiClient.put(`/api/v1/admin/user-permission-groups/${encodeURIComponent(email)}`, { permission_group_ids: groupIds, is_super_admin: isSuperAdmin }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-permission-groups'] }),
  });
}

export function useDeleteUserPermissionGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiClient.delete(`/api/v1/admin/user-permission-groups/${encodeURIComponent(email)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-permission-groups'] }),
  });
}

// --- Current user permissions ---

export function useMyPermissions(enabled = true) {
  return useQuery<ListResponse<MyPermissionsResponse>>({
    queryKey: ['auth', 'my-permissions'],
    queryFn: () => apiClient.get('/api/v1/auth/my-permissions'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled,
  });
}

// --- RBAC-Audit (Super-Admin only): Pro Permission die User auflisten ---

export interface PermissionAuditUser {
  email: string;
  name: string;
  viaGroups: string[];
  isSuperAdmin: boolean;
}

export type PermissionAuditMap = Record<string, PermissionAuditUser[]>;

export function usePermissionAudit(permissions: string[], enabled: boolean) {
  return useQuery<ListResponse<PermissionAuditMap>>({
    queryKey: ['admin', 'permission-audit', [...permissions].sort()],
    queryFn: () => apiClient.post('/api/v1/auth/permissions/users-by-permissions', { permissions }),
    enabled: enabled && permissions.length > 0,
    staleTime: 60 * 1000,
  });
}
